import { useEffect, useState, useRef } from "react";
import chatSocketService from "../../../services/websocket";

export const useChatTyping = (venueUUID, currentUser, inputValue) => {
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef(null);
  const isCurrentlyTypingRef = useRef(false);
  const unsubscribeRef = useRef(null);

  // Typing durumu gönder
  const sendTypingStatus = (isTyping) => {
    if (!currentUser || !venueUUID || !chatSocketService.connected) return;

    try {
      chatSocketService.sendTypingStatus(venueUUID, isTyping);
    } catch (error) {
      console.error("Typing durumu gönderme hatası:", error);
    }
  };

  // Input değişikliklerini izle ve typing durumu gönder
  useEffect(() => {
    if (!currentUser || !venueUUID || !chatSocketService.connected) return;

    const hasContent = inputValue && inputValue.trim().length > 0;

    // Timeout'u her zaman temizle
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (hasContent) {
      // Eğer şu anda typing gösterilmiyorsa, hemen göster
      if (!isCurrentlyTypingRef.current) {
        sendTypingStatus(true);
        isCurrentlyTypingRef.current = true;
      }

      // Her input değişikliğinde timeout'u yenile (1.2 saniye)
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStatus(false);
        isCurrentlyTypingRef.current = false;
      }, 1200);
    } else {
      // Input boşsa hemen typing'i durdur
      if (isCurrentlyTypingRef.current) {
        sendTypingStatus(false);
        isCurrentlyTypingRef.current = false;
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [inputValue, currentUser, venueUUID]);

  // Component unmount olduğunda typing'i temizle
  useEffect(() => {
    return () => {
      if (currentUser?.id && venueUUID && chatSocketService.connected) {
        sendTypingStatus(false);
        isCurrentlyTypingRef.current = false;
      }
    };
  }, [currentUser, venueUUID]);

  // Typing durumlarını dinle
  useEffect(() => {
    if (!venueUUID || !currentUser?.id || !chatSocketService.connected) {
      setTypingUsers({});
      return;
    }

    // Typing eventi listener'ı
    const handleTypingUpdate = (data) => {
      const now = Date.now();
      
      setTypingUsers(prevTypingUsers => {
        const updatedTypingUsers = { ...prevTypingUsers };

        if (data.isTyping && data.user.id !== currentUser.id) {
          // Kullanıcı yazıyor
          updatedTypingUsers[data.user.id] = {
            name: data.user.name || data.user.email,
            avatar: data.user.avatar,
            timestamp: now
          };
        } else {
          // Kullanıcı yazmayı bıraktı veya kendi kullanıcımız
          delete updatedTypingUsers[data.user.id];
        }

        // Eski typing durumlarını temizle (3 saniyeden eski)
        Object.keys(updatedTypingUsers).forEach(userId => {
          if (now - updatedTypingUsers[userId].timestamp > 3000) {
            delete updatedTypingUsers[userId];
          }
        });

        return updatedTypingUsers;
      });
    };

    // Typing listener'ını ekle
    unsubscribeRef.current = chatSocketService.addTypingListener(handleTypingUpdate);

    // Periyodik olarak eski typing durumlarını temizle
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prevTypingUsers => {
        const updatedTypingUsers = { ...prevTypingUsers };
        let hasChanges = false;

        Object.keys(updatedTypingUsers).forEach(userId => {
          if (now - updatedTypingUsers[userId].timestamp > 3000) {
            delete updatedTypingUsers[userId];
            hasChanges = true;
          }
        });

        return hasChanges ? updatedTypingUsers : prevTypingUsers;
      });
    }, 1000);

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      clearInterval(cleanupInterval);
    };
  }, [venueUUID, currentUser?.id]);

  return typingUsers;
};
